import {
  Component,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { UserService } from '@services/user.service';
import {
  CommentingService,
  CommentItem,
  UserItem,
} from '@scaffold/services/commenting.service';
import {
  animate,
  keyframes,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { MentionDirective } from '@app/commenting/mentions/mention.directive';
import { take } from 'rxjs/operators';

@Component({
    selector: 'app-comments-board',
    templateUrl: './comments-board.component.html',
    styleUrls: ['./comments-board.component.scss'],
    animations: [
        trigger('comments', [
            state('in', style({ transform: 'translateX(0)' })),
            transition('void => *', [
                animate('.6s ease-in', keyframes([
                    style({ opacity: 0, transform: 'translateY(-75%)', offset: 0 }),
                    style({ opacity: 0.5, transform: 'translateY(0)', offset: 0.3 }),
                    style({ opacity: 1, transform: 'translateY(0)', offset: 1 }),
                ])),
            ]),
            transition('* => void', [
                animate('.6s ease-in', keyframes([
                    style({ opacity: 1, transform: 'translateY(0)', offset: 0 }),
                    style({ opacity: 0.5, transform: 'translateY(0)', offset: 0.3 }),
                    style({ opacity: 0, transform: 'translateY(75%)', offset: 1 }),
                ])),
            ]),
        ]),
        trigger('replies', [
            state('in', style({ transform: 'translateX(0)' })),
            transition('void => *', [
                animate('.6s ease-in', keyframes([
                    style({ opacity: 0, transform: 'translateY(-75%)', offset: 0 }),
                    style({ opacity: 0.5, transform: 'translateY(0)', offset: 0.3 }),
                    style({ opacity: 1, transform: 'translateY(0)', offset: 1 }),
                ])),
            ]),
            transition('* => void', [
                animate('.6s ease-in', keyframes([
                    style({ opacity: 1, transform: 'translateY(0)', offset: 0 }),
                    style({ opacity: 0.5, transform: 'translateY(0)', offset: 0.3 }),
                    style({ opacity: 0, transform: 'translateY(75%)', offset: 1 }),
                ])),
            ]),
        ]),
        trigger('input', [
            state('in', style({ height: 'auto' })),
            transition('void => *', [
                animate('.3s ease-in', keyframes([
                    style({ opacity: 0, offset: 0 }),
                    style({ opacity: 1, offset: 1 }),
                ])),
            ]),
            transition('* => void', [
                animate('0s ease-in', keyframes([
                    style({ opacity: 1, offset: 0 }),
                    style({ opacity: 0, offset: 1 }),
                ])),
            ]),
        ]),
    ],
    standalone: false
})
export class CommentsBoardComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() info;
  @Input() hideClose: boolean = false;
  @Input() showResolved: boolean = false;
  users: Array<UserItem> = [];
  private _users: Array<UserItem> = [];
  config = {
    labelKey: 'name',
    mentionSelect: this.formatMention,
    disableSearch: true,
  };
  _mention;
  @ViewChild('ment')
  set mention(m: MentionDirective) {
    console.log('SET:', m);
    this._mention = m;
  }
  private _item;
  @Input()
  set item(val) {
    this._item = val;
    console.log('Setting comments to ', val);
    this.comments = [];
    this.commentor
      .getComments(this.info, val)
      .subscribe(c => this.mergeComments(c));
    this.replyingTo = null;
  }
  get item() {
    return this._item;
  }
  @Input() comments = [];
  newComment: any = '';
  replyingTo;
  changeDetection;
  routeSubscription;
  editor_options = {
    inline: true,
    mode: 'exact',
    branding: false,
    browser_spellcheck: true,
    plugins: [
      'advlist lists charmap link',
      'searchreplace wordcount visualblocks visualchars nonbreaking noneditable',
      'image autoresize textcolor table code paste, template' /* BeyondGrammar"*/,
    ],
    advlist_bullet_styles: 'default,circle,disc,square,none',
    paste_auto_cleanup_on_paste: true,
    paste_retain_style_properties:
      'background background-color color font-size',
    allow_script_urls: true,
    remove_script_host: false,
    minHeight: 50,
    convert_urls: false,
    skin: 'lightgray',
    schema: 'html5',
    removed_menuitems: 'copy cut paste',
    toolbar:
      'undo redo | link | styleselect | bold italic | alignmenu | bindent | bullist numlist listindent listoutdent | forecolor backcolor | wrapselection',
    statusbar: true,
    custom_elements:
      'math,maction,maligngroup,malignmark,menclose,merror,mfenced,mfrac,mglyph,mi,mlabeledtr,mlongdiv,mmultiscripts,mn,mo,mover,mpadded,mphantom,mroot,mrow,ms,mscarries,mscarry,msgroup,msline,mspace,msqrt,msrow,mstack,mstyle,msub,msup,msubsup,mtable,mtd,mtext,mtr,munder,munderover,semantics,annotation,annotation-xml,mathvariant,mathsize,mathcolor,mathbackground',
    valid_children: '+span[math],+p[math],+code[ol]',
    extended_valid_elements:
      '@[itemscope|itemtype|itemid|itemprop|content|artifacttype],embed[flashvars|width|height|src|class|id|type],a[onclick|href|name|target|rel|title|class|style|id|download|hreflang|media|type],' +
      'mo[*],mover[*],munderover[*],munder[*],maction[*],mtable[*],mstack[*],math[*],mfrac[*],mfenced[*],mtd[*],mtr[*],mlabeledtr[*],mscarry[*],mstyle[*],mpadded[*],mi[*],mrow[*],ms[*],mtext[*],malignmark[*],maligngroup[*],mglyph[*],mspace[*],' +
      'All[href|id|mathbackground|mathcolor|xlink|href],' +
      'msline[*],mscarries[*],mlongdiv[*],mn[*],menclose[*],msgroup[*],msrow[*],mmultiscripts[*],msub[*],' +
      'div[*],input[*],i[class|title],img[class|src|border=0|alt|title|hspace|vspace|width|height|align|onmouseover|onmouseout|name|aria-describedby|style|onclick]',
    object_resizing: true,
    content_style: 'body:{background-color:#fff;}',
    setup: this.tinySetup.bind(this),
  };

  tinySetup(ed) {
    ed.on('keydown', e => {
      this._zone.run(() => {
        this._mention.keyHandler(e, ed.bodyElement);
      });
    });
  }

  formatMention(item, char) {
    return `<span class="comment-mention" data-mention="${item.id}" contenteditable="false" title="${item.username}">${char}${item.name || item.username}</span> `;
  }

  constructor(
    public userService: UserService,
    public commentor: CommentingService,
    private _zone: NgZone
  ) {}
  ngOnInit() {
    this.userService
      .isLoggedIn()
      .pipe(take(1))
      .subscribe(u => {
        if (u) {
          this.commentor
            .getUsers()
            .pipe(take(1))
            .subscribe((u: [any]) => {
              this._users = u;
              this.users = u;
            });
        }
      });
  }

  searchUser(search) {
    let match = new RegExp('^' + search, 'i');
    this.users = this._users.filter(u => {
      if (!u.username) return false;
      return !!(u.username.match(match) || u.name.match(match));
    });
  }

  ngAfterViewInit() {
    console.log(this._mention);
  }

  ngOnDestroy() {
    if (this.routeSubscription) this.routeSubscription.unsubscribe();
  }

  toDate(time?) {
    if (!time) time = Date.now();
    var date = new Date(time);
    return date.toLocaleString();
  }

  addComment(comment: string, target?, name?) {
    let co = new CommentItem();
    co.text = comment;
    this.commentor
      .addComment(co, this.replyingTo, target, name)
      .subscribe(c => {
        console.log(c);
      });
    this.replyingTo = null;
    this.newComment = '';
  }

  mergeComments(comments: Array<any>) {
    comments.forEach(com => {
      console.log(com);
      if (com.response_to) {
        console.log('reply');
        let reply_to = this.comments.find(
          comment => comment.id === com.response_to
        );
        if (reply_to) {
          if (!reply_to.replies) reply_to.replies = [];
          let found = reply_to.replies.find(comment => comment.id === com.id);
          console.log('found', found);
          if (!found) reply_to.replies.push(com);
        }
      } else {
        let found = this.comments.find(comment => comment.id === com.id);
        console.log('found', found);
        if (!found) {
          this.comments.push(com);
        }
      }
    });
  }

  resolve(comment) {
    if (comment.resolved) {
      this.commentor.updateComment(comment, { reopen: true }).subscribe(_e => {
        comment.resolved = false;
      });
    } else {
      this.commentor.resolveComment(comment).subscribe(_e => {
        comment.resolved = true;
      });
    }
  }

  commentId(_index: number, comment: CommentItem): number {
    return comment.id;
  }
}
